// Color Sampler Tool — places 1-10 persistent color samplers on the canvas.
// Shares shortcut I with Eyedropper.

import { screenToDoc } from "../render/Viewport";
import type { RGB, PointerSample, Tool, ToolContext } from "./Tool";

export interface ColorSampler {
  id: string;
  x: number;
  y: number;
  color: RGB;
  enabled: boolean;
}

export class ColorSamplerTool implements Tool {
  readonly id = "color-sampler";
  readonly shortcut = "I";

  /** Persistent samplers for the current document. */
  samplers: ColorSampler[] = [];

  private placing = false;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = Math.round(p.x), docY = Math.round(p.y);

    // Check if clicking on existing sampler to remove it
    for (let i = this.samplers.length - 1; i >= 0; i--) {
      const sp = this.samplers[i]!;
      if (Math.abs(sp.x - docX) < 4 && Math.abs(sp.y - docY) < 4) {
        this.samplers.splice(i, 1);
        ctx.requestRender();
        return;
      }
    }

    // Add new sampler (max 10)
    if (this.samplers.length >= 10) return;

    const color = this.sampleAt(ctx, docX, docY);
    this.samplers.push({
      id: crypto.randomUUID(),
      x: docX, y: docY,
      color, enabled: true,
    });
    this.placing = true;
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.placing || !ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = Math.round(p.x), docY = Math.round(p.y);
    const last = this.samplers[this.samplers.length - 1];
    if (last) {
      last.x = docX;
      last.y = docY;
      last.color = this.sampleAt(ctx, docX, docY);
    }
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    this.placing = false;
  }

  private sampleAt(ctx: ToolContext, x: number, y: number): RGB {
    if (!ctx.doc) return { r: 0, g: 0, b: 0 };
    // Sample from the topmost visible raster layer at this position
    for (let i = ctx.doc.layers.length - 1; i >= 0; i--) {
      const layer = ctx.doc.layers[i]!;
      if (layer.kind !== "raster" || !layer.visible) continue;
      const lx = x - layer.x, ly = y - layer.y;
      if (lx < 0 || ly < 0 || lx >= layer.pixels.width || ly >= layer.pixels.height) continue;
      const idx = (ly * layer.pixels.width + lx) * 4;
      const a = layer.pixels.data[idx + 3]!;
      if (a === 0) continue;
      return {
        r: layer.pixels.data[idx]!,
        g: layer.pixels.data[idx + 1]!,
        b: layer.pixels.data[idx + 2]!,
      };
    }
    return { r: 0, g: 0, b: 0 };
  }

  /** Clear all samplers. */
  clearAll(): void {
    this.samplers = [];
  }
}
